import { useEffect, useState } from 'react'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated, selectUser } from '@/features/auth/authSlice'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { getCompanies, getProperties } from '@/features/invitee/services/inviteeService'
import { useUserRole } from '@/hooks/useUserRole'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { ReportDateRangePicker } from '../ReportDateRangePicker'
import type { BalanceReportBalanceReportFiltersProps, BalanceReportListParams } from '../../types/pages'
import { LABEL_CLASS, SELECT_CLASS } from '../../utils/balanceReportPageHelpers'

export function BalanceReportFilters({
  filters,
  isOpen,
  onClose,
  onApply,
  onReset,
}: BalanceReportBalanceReportFiltersProps) {
  const user = useAppSelector(selectUser)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const { companyId, propertyId } = useUserRole()
  const lockedCompanyId = companyId != null ? String(companyId) : undefined
  const lockedPropertyId = propertyId != null ? String(propertyId) : undefined

  const [draft, setDraft] = useState<BalanceReportListParams>(filters)
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
  const [properties, setProperties] = useState<SearchableSelectOption[]>([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingProperties, setLoadingProperties] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const amount = filters.payment_amount
      const n = amount === '' || amount == null ? NaN : Number(amount)
      setDraft({
        ...filters,
        payment_amount:
          amount === '' || amount == null
            ? amount ?? ''
            : Number.isNaN(n) || n < 0
              ? ''
              : amount,
      })
    }
  }, [isOpen, filters])

  const loadCompanies = async (search: string) => {
    if (!isAuthenticated || lockedCompanyId) return
    setLoadingCompanies(true)
    try {
      const data = await getCompanies(search)
      setCompanies(
        data.map((c) => ({
          value: String(c.company_id),
          label: c.company_name || String(c.company_id),
        })),
      )
    } finally {
      setLoadingCompanies(false)
    }
  }

  const loadProperties = async (search: string) => {
    if (!isAuthenticated || lockedPropertyId) return
    setLoadingProperties(true)
    try {
      const data = await getProperties(
        search,
        draft.company_id || lockedCompanyId,
      )
      setProperties(
        data.map((p) => ({
          value: String(p.property_id),
          label: p.property_name || String(p.property_id),
        })),
      )
    } finally {
      setLoadingProperties(false)
    }
  }

  useEffect(() => {
    if (!isAuthenticated || !isOpen) return
    if (!lockedCompanyId) void loadCompanies('')
    if (!lockedPropertyId) void loadProperties('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isOpen,
    lockedCompanyId,
    lockedPropertyId,
    draft.company_id,
  ])

  const patch = (partial: Partial<BalanceReportListParams>) =>
    setDraft((prev) => ({ ...prev, ...partial }))
  const companyOptions = lockedCompanyId
    ? [
        {
          value: lockedCompanyId,
          label: user?.current_role?.company_name || lockedCompanyId,
        },
      ]
    : companies
  const propertyOptions = lockedPropertyId
    ? [
        {
          value: lockedPropertyId,
          label: user?.current_role?.property_name || lockedPropertyId,
        },
      ]
    : properties

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={onReset}
      onDone={() => {
        const amount = draft.payment_amount
        const n = amount === '' || amount == null ? NaN : Number(amount)
        const payment_amount =
          amount === '' || amount == null
            ? amount ?? ''
            : Number.isNaN(n) || n < 0
              ? ''
              : amount
        onApply({
          ...draft,
          payment_amount,
          company_id: lockedCompanyId || draft.company_id,
          property_id: lockedPropertyId || draft.property_id,
        })
        onClose()
      }}
      testIdPrefix="balance-report"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className={LABEL_CLASS}>Company</label>
          <SearchableSelect
            options={companyOptions}
            value={lockedCompanyId || draft.company_id || ''}
            onChange={(val) => {
              const company_id = Array.isArray(val) ? (val[0] ?? '') : val
              patch({ company_id, property_id: '' })
            }}
            onSearch={lockedCompanyId ? undefined : loadCompanies}
            onOpen={lockedCompanyId ? undefined : () => loadCompanies('')}
            loading={loadingCompanies}
            disabled={Boolean(lockedCompanyId)}
            placeholder="All Companies"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Property</label>
          <SearchableSelect
            options={propertyOptions}
            value={lockedPropertyId || draft.property_id || ''}
            onChange={(val) => {
              const property_id = Array.isArray(val) ? (val[0] ?? '') : val
              patch({ property_id })
            }}
            onSearch={lockedPropertyId ? undefined : loadProperties}
            onOpen={lockedPropertyId ? undefined : () => loadProperties('')}
            loading={loadingProperties}
            disabled={Boolean(lockedPropertyId)}
            placeholder="All Properties"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Application Status</label>
          <SearchableSelect
            searchable={false}
            options={[{ value: 'APPROVED', label: 'Approved' }]}
            value={draft.application_status}
            onChange={(val) => {
              const v = Array.isArray(val) ? val[0] : val
              patch({ application_status: v || '' })
            }}
            placeholder="Application Status"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Balance Due</label>
          <div className="grid grid-cols-[7rem_1fr] gap-2">
            <SearchableSelect
              searchable={false}
              options={[
                { value: '<', label: '<' },
                { value: '=', label: '=' },
                { value: '>', label: '>' },
              ]}
              value={draft.amount_condition}
              onChange={(val) => {
                const v = Array.isArray(val) ? val[0] : val
                patch({ amount_condition: v || '' })
              }}
              placeholder="Any"
            />
            <input
              type="number"
              min={0}
              step="any"
              className={`${SELECT_CLASS} no-spinners`}
              value={draft.payment_amount}
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '' || raw === '.') {
                  patch({ payment_amount: raw })
                  return
                }
                const n = Number(raw)
                if (Number.isNaN(n) || n < 0) return
                patch({ payment_amount: raw })
              }}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                  e.preventDefault()
                }
              }}
              onPaste={(e) => {
                const text = e.clipboardData.getData('text')
                if (text.includes('-') || Number(text) < 0) {
                  e.preventDefault()
                }
              }}
              placeholder="Amount"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Type</label>
          <SearchableSelect
            searchable={false}
            options={[
              { value: 'CONTRACTS', label: 'Contracts' },
              { value: 'BALANCE_ADJUSTMENT', label: 'Balance Adjustment' },
            ]}
            value={draft.chargeback_type}
            onChange={(val) => {
              const v = Array.isArray(val) ? val[0] : val
              patch({ chargeback_type: v || '' })
            }}
            placeholder="Type"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Date Range</label>
          <ReportDateRangePicker
            startDate={draft.fromDt}
            endDate={draft.toDt}
            onChange={(start, end) =>
              patch({
                fromDt: start ? formatDateForAPI(start) : '',
                toDt: end ? formatDateForAPI(end) : '',
              })
            }
          />
        </div>
      </div>
    </ListFilterDrawer>
  )
}
